import React, { Fragment, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import './delineateWatershedInput.scss';
import { DelineateWatershedInputStateVM } from './DelineateWatershedInputStateVM';
import Icon from '@mdi/react';
import { mdiCloudUploadOutline, mdiClose } from '@mdi/js';
import EsriWidgetHost from '../../common/mapping/EsriWidgetHost/EsriWidgetHost';
import { FlatButton } from 'nrcs-design-system';
import { ToggleButton } from 'cd-eft/components/common/ToggleButton/ToggleButton';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { CustomInput, Table } from 'reactstrap';
import { ToolSlot } from 'cd-eft/components/Design/BreaklineTools/framework/Tool';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { useRerender } from 'cd-eft/hooks/useRerender';
import { WatershedSmartCutTool } from './WatershedSmartCutTool';
import Graphic from '@arcgis/core/Graphic';
import { reset24, undo24, redo24, line24 } from "@esri/calcite-ui-icons";
import { RadioGroup } from 'cd-eft/components/common/RadioGroup';
import { Accordion } from 'cd-eft/components/common/Accordion';
import { SelectOption } from 'cd-eft/components/common/SelectOption';
import { VelocityMethodVM } from './VelocityMethod/VelocityMethodVM';
import { MethodTypeSelection, VelocityAccordion } from './VelocityMethod/VelocityMethod';

const AppStore = stores.AppStore;

interface IDelineateWatershedInputComponentProps {
  vm?: DelineateWatershedInputStateVM;
  history?: any;
}

const DelineateWatershedInputComponent = (props: IDelineateWatershedInputComponentProps) => {
  const vm = props.vm;

  if (!vm) {
    return null;
  }

  return (
    <div className="container px-2">
      <form
        className={`DelineateWatershedInputComponent${vm.currentStepIndex === 2 &&
          AppStore.userProfileStore.applicationFlags.enablevelocity ? ' wider-container' : ''}`}
        data-testid="DelineateWatershedInputComponent-form"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        {!vm.jobExecuting ? (
          <fieldset>
            {/* New Combined step 1 DEM & AOI */}
            {vm.currentStepIndex === 0 ? (
              <Fragment>
                {/* Address search bar */}
                <SearchBar vm={vm} />
                <hr />
                <AoiSketch vm={vm} />
                {/* contours? */}
                {/* "Create the following:" section  */}
                <LayerOptionsArea vm={vm} />
                <DemFieldSet vm={vm} />
              </Fragment>
            ) : null}
            {/* End combined step */}

            {/* Step 3: Define Outlet */}
            {vm.currentStepIndex === 1 ? (
              <Fragment>
                <OutletSketch vm={vm} />
                <CulvertSketch vm={vm} />
              </Fragment>
            ) : null}
            {/* Step 3: Define Outlet End */}

            {/* Step 4: Edit Watershed */}
            {vm.currentStepIndex === 2 ? (
              <EditWatershedContainer vm={vm} />
            ) : null}
            {/* Step 4: Edit Watershed End */}
          </fieldset>
        ) : null}
      </form>
    </div>
  );
};
export const DelineateWatershedInput = observer(DelineateWatershedInputComponent);


const EditWatershedContainer = observer((props: IDelineateWatershedInputComponentProps) => {
  const { vm } = props;
  const velocityVmRef = useRef<VelocityMethodVM | null | undefined>(vm?.velocityVm);
  if (!velocityVmRef.current) {
    velocityVmRef.current = new VelocityMethodVM(props);
    vm!.velocityVm = velocityVmRef.current;
  }
  const velocityVm = velocityVmRef.current;
  if (AppStore.userProfileStore.applicationFlags.enablevelocity) {
    return (
      <Fragment>
        <div className='row mx-0 column-inputs'>
          <div className='col col-sm-4'>
            <WatershedSketch vm={vm} />
            <FlowPathSketch vm={vm} />
            <SmartCutEditor vm={vm} />
          </div>
          <div className='col col-sm-4'>
            <FlowPathLength vm={vm} />
            <MethodTypeSelection vm={velocityVm} />
          </div>
        </div>
        <div className='row mx-0 velocity-content-container' hidden={velocityVm.lagSelected || AppStore.userProfileStore.applicationFlags.enablevelocity !== true}>
          <VelocityAccordion vm={velocityVm} />
        </div>
      </Fragment>
    );
  } else {
    return (
      <Fragment>
        <div className='row mx-0 column-inputs'>
          <div className='col'>
            <WatershedSketch vm={vm} />
            <FlowPathSketch vm={vm} />
            <FlowPathLength vm={vm} />
            <SmartCutEditor vm={vm} />
          </div>
        </div>
      </Fragment>
    );
  }
});


const DemFileInput = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row ">
      <div className="col">
        <div className="usa-file-input__target py-1">
          <div className="usa-file-input">
            <Icon path={mdiCloudUploadOutline} title="upload" size={1.5} color="#005ea2" />
            <div className="usa-file-input_instructions">
              <span className="usa-file-input__drag-text" id="usa-file-input__drag-text">
                Drag <strong>.tif file</strong> here or browse.
              </span>
            </div>

            <div className="usa-file-input__box"></div>
            <input
              id="inFile"
              data-testid="inFile"
              className="usa-file-input__input"
              type="file"
              name="file"
              aria-describedby="usa-file-input__drag-text"
              accept=".tif"
              multiple={false}
              onChange={(e) => {
                const value: File = (e.target.files as FileList)[0];
                props.vm!.in_dem_file = value;
                props.vm!.in_dem_elevation_units = '';
              }}
            ></input>
          </div>
        </div>
        <div className="d-inline-flex justify-content-between uploaded-file-div w-100">
          <div className="py-1 uploaded-file-name" hidden={props.vm!.in_dem_file === undefined}>
            File: <strong>{props.vm!.in_dem_file?.name}</strong>
          </div>
          <FlatButton
            iconOnly={mdiClose}
            title="Reject DEM"
            data-testid="reject_dem"
            hidden={props.vm!.in_dem_file === undefined}
            onClick={() => {
              props.vm!.clearDemGraphics();
              const oldInput = document.getElementById('inFile') as HTMLInputElement;
              oldInput.files = null;
              oldInput.value = '';
            }}
          />
        </div>
      </div>
    </div>
  );
});

const ElevationUnitsSelect = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label mr-2" htmlFor="in_dem_elevation_units">
          Elevation Units
        </label>
        <div className="select-dropdown">
          <select
            disabled={!props.vm?.in_dem_file}
            className="usa-select margin-top-0 display: inline-flex"
            name="in_dem_elevation_units"
            id="in_dem_elevation_units"
            data-testid="in_dem_elevation_units"
            value={props.vm!.in_dem_elevation_units}
            onChange={(e) => {
              const value: string = e.target.value;
              props.vm!.in_dem_elevation_units = value;
              props.vm!.disableServiceDems();
              props.vm!.result_dem = undefined;
              props.vm?.demSubmit(); // Comment this out so it doesn't auto go to the next step if aoi defined. Assumes uploaded dems have known areas.
            }}
          >
            <option value="" label="None">
              None
            </option>
            <option value="centimeters" label="centimeters">
              centimeters
            </option>
            <option value="meters" label="meters">
              meters
            </option>
            <option value="inches" label="inches">
              inches
            </option>
            <option value="feet" label="feet">
              feet
            </option>
          </select>
        </div>
      </div>
    </div>
  );
});

const SearchBar = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row">
      <div className="col search-widget">
        <div data-testid="search_bar" className={'express-mode'}>
          <EsriWidgetHost esriWidget={props.vm!.searchBar} idAndName={'searchWidget'} />
        </div>
      </div>
    </div>
  );
});

const AoiSketch = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label" htmlFor="areaOfInterest">
          Draw Area Of Interest (AOI)
        </label>

        <div data-testid="aoi_sketch" className={'d-inline-block express-mode'}>
          <EsriWidgetHost esriWidget={props.vm!.aoiSketch} idAndName={'areaOfInterest'} showAoiArea={true} aoiArea={props.vm?.aoiArea} />
        </div>
      </div>
    </div>
  );
});

const CulvertSketch = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label mr-2" htmlFor="hydraulicConnections">
          Draw Hydraulic Connections
        </label>

        <div data-testid="culvert_sketch" className={'d-inline-block express-mode'}>
          <EsriWidgetHost esriWidget={props.vm!.culvertSketch} idAndName={'hydraulicConnections'} />
        </div>
      </div>
    </div>
  );
});
const OutletSketch = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label mr-2" htmlFor="drawOutlet">
          Draw Outlet
        </label>

        <div data-testid="outlet_sketch" className={'d-inline-block express-mode'}>
          <EsriWidgetHost esriWidget={props.vm!.outletSketch} idAndName={'drawOutlet'} />
        </div>
      </div>
    </div>
  );
});

const WatershedSketch = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row">
      <div className="col d-inline-block">
        <label className="select-label mr-2 watershed-label" htmlFor="editWatershed">
          Edit Watershed
        </label>

        <CustomInput
          type="switch"
          id="EditWatershed"
          data-testid="EditWatershed"
          name="EditWatershed"
          checked={props.vm!.isEditWatershedOn}
          onChange={props.vm!.editWatershedSwitchHandler}
        />

        <div data-testid="watershed_sketch" className="d-inline-block express-mode">
          <EsriWidgetHost esriWidget={props.vm!.watershedSketch} idAndName={'editWatershed'} />
        </div>
      </div>
    </div>
  );
});

const FlowPathSketch = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div>
      <div className="row">
        <div className="col d-inline-block">
          <label className="select-label mr-2 watershed-label" htmlFor="editFlowPath">
            Edit Flow Path
          </label>

          <CustomInput
            type="switch"
            id="EditFlowPath"
            data-testid="EditFlowPath"
            name="EditFlowPath"
            checked={props.vm!.isEditFlowPathOn}
            onChange={props.vm!.editFlowPathSwitchHandler}
          />

          <div data-testid="flowpath_sketch" className="d-inline-block express-mode">
            <div className="button-container">
              <button
                id="digitize-button"
                className="edit-watershed-button"
                aria-label="Digitize"
                title="Draw a Polyline"
                disabled={props.vm!.createPolylineDisabled}
                onClick={props.vm!.onclickCreateFlowPath}>
                <Icon path={line24} title={'Draw a Polyline'} className="edit-watershed-icon" />
              </button>
              <button
                id="undo-button"
                className="edit-watershed-button"
                aria-label="Undo"
                title="Undo"
                disabled={!props.vm!.canUndoFlowPath}
                onClick={props.vm!.onClickUndoFlowPath}>
                <Icon path={undo24} title={'Undo'} className="edit-watershed-icon" />
              </button>
              <button
                id="redo-button"
                className="edit-watershed-button"
                aria-label="Redo"
                title="Redo"
                disabled={!props.vm!.canRedoFlowPath}
                onClick={props.vm!.onClickRedoFlowPath}>
                <Icon path={redo24} title={'Redo'} className="edit-watershed-icon" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const FlowPathLength = observer(({ vm }: { vm: DelineateWatershedInputStateVM | undefined }) => {
  return (
    <div className="row">
      <div className="col flow-length-display-col">
        <label className="select-label mr-2 watershed-label">
          Flow Path Length (ft)
        </label>
        <div className="d-inline-block express-mode-fl">
          <input
            className="length-input"
            id="flowpath-length"
            type="text"
            name="flowpath-length"
            readOnly={true}
            value={vm!.flowPathLength}
          />
        </div>
      </div>
    </div>
  )
})

const SmartCutEditor = observer((props: IDelineateWatershedInputComponentProps) => {
  const rerender = useRerender();

  return (
    <div>
      <div className="row">
        <div className="col d-inline-block">
          <label className="select-label mr-2 watershed-label" htmlFor="smartCut">
            Smart Cut
          </label>

          <div data-testid="smart_cut" className="d-inline-block express-mode">
            <div className="button-container">
              <button
                id="digitize-button"
                className="edit-watershed-button"
                title="Draw a Polyline"
                onClick={async () => {
                  if (props.vm!.watershedGraphicsLayer) {
                    const mapView = props.vm!.mapView;
                    const toolSlot = new ToolSlot();
                    const watershedPolygonLayer = props.vm!.watershedGraphicsLayer as GraphicsLayer;
                    const outletPolylineLayer = new GraphicsLayer();

                    props.vm!.outletsFeature?.features.forEach((feature) => {
                      const graphic = new Graphic({
                        geometry: feature.geometry,
                        attributes: feature.attributes,
                        symbol: feature.symbol || undefined,
                      });

                      outletPolylineLayer.add(graphic);
                    });
                    const tool = new WatershedSmartCutTool(mapView, toolSlot, watershedPolygonLayer, outletPolylineLayer, props.vm!.resetHydrologyValidationError);
                    await tool.activate();
                    const polygonsDeleted = tool.wasPolygonsDeleted();

                    if (polygonsDeleted) {
                      props.vm!.watershedModified = polygonsDeleted;
                      props.vm!.canResetSmartCut = true;
                      props.vm!.updateWatershedSymbol();
                    }

                    rerender();
                  }
                }}
              >
                <Icon path={line24} title={'Draw a Polyline'} className="edit-watershed-icon" />
              </button>
              <button
                id="reset-button"
                className="edit-watershed-button"
                aria-label="Reset"
                title="Reset All"
                disabled={!props.vm!.canResetSmartCut}
                onClick={props.vm!.onClickResetSmartCut}>
                <Icon path={reset24} title={'Reset All'} className="edit-watershed-icon" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const HillshadeToggle = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div>
      <ToggleButton
        className={'create-layer'}
        callback={() => {
          props.vm!.in_generate_hillshade = !props.vm!.in_generate_hillshade;
        }}
        title={'Hillshade'}
        flag={props.vm!.in_generate_hillshade}
        id={'in_generate_hillshade'}
      />
    </div>
  );
});

const DepthgridToggle = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div>
      <ToggleButton
        className={'create-layer'}
        callback={() => {
          props.vm!.in_generate_depthgrid = !props.vm!.in_generate_depthgrid;
        }}
        title={'Depthgrid'}
        flag={props.vm!.in_generate_depthgrid}
        id={'in_generate_depthgrid'}
      />
    </div>
  );
});

const ContoursToggle = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div>
      <ToggleButton
        className={'create-layer'}
        callback={() => {
          props.vm!.in_generate_contours = !props.vm!.in_generate_contours;
        }}
        title={'Contours'}
        flag={props.vm!.in_generate_contours}
        id={'in_generate_contours'}
      />
    </div>
  );
});

const ContourIntervalSelect = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label mr-1" htmlFor="in_contour_interval">
          Contour&nbsp;Interval&nbsp;&nbsp;&nbsp;&nbsp;
        </label>
      </div>
      <div className="col select-dropdown">
        <select
          disabled={props.vm!.in_generate_contours ? false : true}
          className="usa-select margin-top-0"
          name="in_contour_interval"
          id="in_contour_interval"
          data-testid="in_contour_interval"
          value={props.vm!.in_contour_interval}
          onChange={(e) => {
            const value: number = parseFloat(e.target.value);
            props.vm!.in_contour_interval = value;
          }}
        >
          <option value="0.5" label="0.5 ft">
            0.5 ft
          </option>
          <option value="1" label="1.0 ft">
            1.0 ft
          </option>
          <option value="2" label="2.0 ft">
            2.0 ft
          </option>
          <option value="5" label="5.0 ft">
            5.0 ft
          </option>
          <option value="10" label="10.0 ft">
            10.0 ft
          </option>
        </select>
      </div>
    </div>
  );
});

const StreamlinesToggle = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div>
      <ToggleButton
        flag={props.vm!.in_generate_streamlines}
        callback={() => {
          props.vm!.in_generate_streamlines = !props.vm!.in_generate_streamlines;
        }}
        id={'in_generate_streamlines'}
        className={'create-layer'}
        title={'Streamlines'}
      />
    </div>
  );
});

const StreamThresholdSelect = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="row">
      <div className="col">
        <label className="select-label mr-1" htmlFor="in_stream_threshold">
          Stream&nbsp;Threshold&nbsp;
        </label>
      </div>
      <div className="col select-dropdown">
        <select
          disabled={props.vm!.in_generate_streamlines ? false : true}
          className="usa-select margin-top-0"
          name="in_stream_threshold"
          id="in_stream_threshold"
          data-testid="in_stream_threshold"
          value={props.vm!.in_stream_threshold}
          onChange={(e) => {
            const value: number = parseFloat(e.target.value);
            props.vm!.in_stream_threshold = value;
          }}
        >
          <option value="0.25">0.25 acres</option>
          <option value="0.5">0.5 acres</option>
          <option value="1">1.0 acres</option>
          <option value="2">2.0 acres</option>
          <option value="4">4.0 acres</option>
          <option value="8">8.0 acres</option>
        </select>
      </div>
    </div>
  );
});

const LayerOptionsArea = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="col">
      <div className="row">Create the following:</div>
      <div className="row">
        <HillshadeToggle vm={props.vm!} />

        <ContoursToggle vm={props.vm} />

        <ContourIntervalSelect vm={props.vm} />
      </div>
      <div className="row">
        <DepthgridToggle vm={props.vm!} />

        <StreamlinesToggle vm={props.vm} />

        <StreamThresholdSelect vm={props.vm} />
      </div>
    </div>
  );
});

const DemFieldSet = observer((props: IDelineateWatershedInputComponentProps) => {
  return (
    <div className="usa-fieldset">
      <legend className="usa-legend">Define DEM:</legend>
      <button
        className="usa-button"
        onClick={async () => {
          await props.vm?.createDemImageLayers();
          await props.vm?.tryDemServicesAvailability();
        }}
        disabled={!AppStore.userProfileStore.applicationFlags.enabledemsearch}
      >
        Reload DEM Services
      </button>
      <div className="usa-radio">
        <input
          disabled={!AppStore.userProfileStore.applicationFlags.enabledemsearch}
          className="usa-radio__input usa-radio__input--tile"
          id="dem-service"
          type="radio"
          name="dem-service"
          data-testid="dem-service"
          value="service"
          checked={props.vm!.in_dem_service_selection === 'service'}
          onChange={() => {
            props.vm!.in_dem_service_selection = 'service';
            props.vm!.in_dem_title = '';
            props.vm!.checkDemServicesAvailability();
          }}
        />
        <label className="usa-radio__label" htmlFor="dem-service">
          Search for available DEM Service
        </label>
      </div>
      <div className="dem-service-content" hidden={props.vm?.in_dem_service_selection !== 'service'}>

        <select
          className="usa-select margin-top-0"
          name="in_stream_threshold"
          id="dem-service-resolution"
          data-testid="dem-service-resolution"
          disabled={!props.vm?.isDemServiceAvailable}
          onChange={(e) => {
            const value: string = e.target.value;
            props.vm!.in_dem_id = value;
            props.vm?.selectDemService();
          }}
        >
          <option value=""> - select - </option>
          {props.vm?.availableDemServices
            ? props.vm?.availableDemServices.map((el) => (
              <option
                value={el.id}
                key={el.id}
                className={el.notEligible ? 'highlightedRow usa-tooltip' : ''}
                title={el.notEligible ? MessageCenter.getFromCache('Hydro_67') : ''}
              >
                {el.notEligible ? `${el.title} \u24d8` : el.title}
              </option>
            ))
            : null}

        </select>
        <button
          className="usa-button"
          onClick={props.vm?.openDemUrl}
          disabled={!(props.vm?.isDemServiceAvailable)}
        >
          View Metadata
        </button>
        <button
          className="usa-button"
          onClick={() => {
            // props.vm?.demClip();
            props.vm?.previewDem();
          }}
          disabled={!(props.vm?.isDemServiceAvailable)}
        >
          Preview DEM
        </button>
      </div>
      <div className="usa-radio">
        <input
          className="usa-radio__input usa-radio__input--tile"
          id="dem-upload"
          data-testid="dem-upload"
          type="radio"
          name="dem-upload"
          value="upload"
          checked={props.vm!.in_dem_service_selection === 'upload'}
          onChange={() => {
            console.warn('SELECT UPLOAD DEM OPTION');
            props.vm!.in_dem_service_selection = 'upload';
            props.vm!.in_dem_title = '';
            props.vm!.in_dem_hillshade = "None";
          }}
        />
        <label className="usa-radio__label" htmlFor="dem-upload">
          Upload DEM
        </label>
      </div>
      <div hidden={props.vm?.in_dem_service_selection !== 'upload'} className="row">
        <div className="col-md-8">
          <DemFileInput vm={props.vm} />
        </div>
        <div className="col">
          <ElevationUnitsSelect vm={props.vm} />
        </div>
      </div>
    </div>
  );
});
